// 🚚 택배송장 입력 다이얼로그 컴포넌트
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

interface TrackingDialogProps {
  open: boolean;
  trackingType: 'delivery' | 'return';
  trackingNumber: string;
  trackingSubmitting: boolean;
  onClose: () => void;
  onTrackingNumberChange: (value: string) => void;
  onSave: () => Promise<void>;
}

/**
 * 택배송장 입력 다이얼로그 컴포넌트
 * - 발송/반품 송장번호 입력
 * - 저장 및 취소 기능
 */
export const TrackingDialog: React.FC<TrackingDialogProps> = ({
  open,
  trackingType,
  trackingNumber,
  trackingSubmitting,
  onClose,
  onTrackingNumberChange,
  onSave,
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(99, 102, 241, 0.1)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: trackingType === 'delivery' 
            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(14, 165, 233, 0.06) 100%)'
            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(251, 146, 60, 0.06) 100%)',
          borderBottom: `1px solid ${trackingType === 'delivery' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(239, 68, 68, 0.1)'}`,
          fontWeight: 700,
          color: '#374151',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {trackingType === 'delivery' ? '📦 택배송장번호 관리' : '📦 반품송장번호 관리'}
        </Typography>
        <IconButton 
          onClick={onClose}
          size="small"
          sx={{ color: '#6b7280' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.6 }}>
          {trackingType === 'delivery' 
            ? '고객이 기기를 발송한 택배송장번호를 입력해주세요. CJ대한통운 송장번호를 권장합니다.'
            : '고객에게 반품 발송한 택배송장번호를 입력해주세요.'}
        </Typography>
        <TextField
          autoFocus
          label={trackingType === 'delivery' ? '택배송장번호' : '반품송장번호'}
          fullWidth
          variant="outlined"
          value={trackingNumber}
          onChange={(e) => onTrackingNumberChange(e.target.value)}
          placeholder="예: 123456789012"
          helperText="숫자만 입력해주세요 (하이픈 제외)"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: trackingType === 'delivery' ? '#6366f1' : '#ef4444',
                borderWidth: 2,
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: trackingType === 'delivery' ? '#6366f1' : '#ef4444',
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1,
            borderColor: 'rgba(107, 114, 128, 0.3)',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#6b7280',
              bgcolor: 'rgba(107, 114, 128, 0.05)',
            }
          }}
        >
          취소
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={!trackingNumber.trim() || trackingSubmitting}
          sx={{
            borderRadius: 3,
            px: 4,
            py: 1,
            fontWeight: 700,
            background: trackingType === 'delivery' 
              ? 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
            boxShadow: trackingType === 'delivery'
              ? '0 4px 16px rgba(99, 102, 241, 0.3)'
              : '0 4px 16px rgba(239, 68, 68, 0.3)',
            '&:hover': {
              background: trackingType === 'delivery'
                ? 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)'
                : 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
              boxShadow: trackingType === 'delivery'
                ? '0 6px 20px rgba(99, 102, 241, 0.4)'
                : '0 6px 20px rgba(239, 68, 68, 0.4)',
            },
            '&.Mui-disabled': {
              background: 'linear-gradient(135deg, #e5e7eb 0%, #f3f4f6 100%)',
              color: '#9ca3af',
            }
          }}
        >
          {trackingSubmitting ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};